import { useTranslation } from 'react-i18next'

import { Text } from '@enechain/ecloud-designsystem'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useDeleteSplitSupplyContract } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/hooks/useDeleteSplitSupplyContract'
import type { SplitSupplyContractEditSnapshot } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/types'
import { mapMasterDeleteError } from '~/pages/AdminOcctoMaster/tabs/_common/deleteError'

type Props = {
  isOpen: boolean
  snapshot: SplitSupplyContractEditSnapshot
  onClose: () => void
  onError: (message: string) => void
  errorMessage: string | undefined
}

/**
 * 分割供給契約の削除確認ダイアログ。 契約と配下の基本パターンが消える。
 * 本文は spec AC-OMSS-004 (1 文目は AC-MOI-019 の統一書式、 2 文目は配下が 1 件以上の時だけ。
 * spec の「計算ルール」は本番の基本パターン)。 件数は編集モーダルが取得した契約 1 件の cache から読む。
 * 楽観ロックの失敗などは onError で親にメッセージを返し、 同じダイアログに出す。
 */
export const DeleteSplitSupplyContractDialog = ({
  isOpen,
  snapshot,
  onClose,
  onError,
  errorMessage,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useDeleteSplitSupplyContract()
  const { contract } = snapshot
  const patternCount = contract.basePatterns.length

  const handleConfirm = async (): Promise<void> => {
    try {
      await mutateAsync({
        supplyPointSpecificNumber: contract.supplyPointSpecificNumber,
        expectedUpdatedAt: contract.updatedAt,
      })
      onClose()
      showSuccessToast({ title: t('common:toast.deleted') })
    } catch (err) {
      onError(
        mapMasterDeleteError(
          t,
          err,
          'page:adminOcctoMaster.splitSupplyContract.serverError',
        ),
      )
    }
  }

  return (
    <ConfirmDialog
      body={
        errorMessage ?? (
          <>
            <Text>
              {t(
                'page:adminOcctoMaster.splitSupplyContract.delete.confirmBody',
                {
                  supplyPointSpecificNumber: contract.supplyPointSpecificNumber,
                },
              )}
            </Text>
            {patternCount > 0 && (
              <Text marginTop={2} color="Text.warningHigh">
                {t(
                  'page:adminOcctoMaster.splitSupplyContract.delete.patternWarning',
                  { count: patternCount },
                )}
              </Text>
            )}
          </>
        )
      }
      confirmLabel={t(
        'page:adminOcctoMaster.splitSupplyContract.delete.confirmLabel',
      )}
      destructive
      isLoading={isPending}
      isOpen={isOpen}
      onCancel={onClose}
      onConfirm={() => {
        void handleConfirm()
      }}
      title={t('page:adminOcctoMaster.splitSupplyContract.delete.confirmTitle')}
    />
  )
}
